// src/pages/Futures/Futures.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Typography, Skeleton, useTheme, useMediaQuery, Snackbar, Alert, Grid, LinearProgress, Paper, IconButton, Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { jwtDecode } from 'jwt-decode';
import FuturesOrderForm from './OrderForm';
import FuturesOrderBook from './FuturesOrderBook';
import FuturesTradeTabs from './FuturesTradeTabs';
import TradingViewWidget from './FuturesTradingViewWidget';
import FuturesCoinDetails from './FuturesCoinDetails';
import { fetchSpotTradingPairs, fetchWalletIds } from '../../store/Slices/assetSlice';
import { setSelectedCoin } from '../../store/Slices/FuturesTradingSlice';
import { initializeSocket, disconnectSocket } from '../../services/futuresSocket';

import TopNav from '../../Components/NavComponents/TopNav';
import BottomNav from '../../Components/NavComponents/BottomNav';

const COLORS = {
  YELLOW: '#F0B90B',
  GREEN: '#0ECB81',
  RED: '#F6465D',
};

const Futures = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isSmDown = useMediaQuery(theme.breakpoints.down('md'));

  const { spotTradingPairs, walletIds } = useSelector((state) => state.assets);
  const { selectedCoin, error: futuresError } = useSelector((state) => state.futures);
  const { data: user, loading: userLoading } = useSelector((state) => state.user);

  const [tpLoading, setTpLoading] = useState(false);
  const [tpError, setTpError] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState(null);
  const [hasInit, setHasInit] = useState(false);
  const [socketError, setSocketError] = useState(null);
  const [fetching, setFetching] = useState(false);

  // SOCKET
  useEffect(() => {
    if (!user?._id) return;
    const sock = initializeSocket({ dispatch, userId: user._id });
    sock.on('connect_error', (err) => {
      setSocketError('Failed to connect to WebSocket.');
      console.error('Socket error:', err);
    });
    return () => disconnectSocket();
  }, [dispatch, user?._id]);

  // Initial Data with robust checks
  const getAuthOk = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      if ((Date.now() / 1000) > decoded.exp) return false;
      return true;
    } catch {
      return false;
    }
  }, []);

  const fetchAll = useCallback(async () => {
    if (hasInit || userLoading) return;
    setHasInit(true);
    if (!getAuthOk()) {
      setTpError('Please log in.');
      setWalletError('Please log in.');
      window.location.href = '/login';
      return;
    }
    if (!user?._id) {
      setTpError('User not loaded yet.');
      setWalletError('User not loaded yet.');
      return;
    }

    setFetching(true);
    // Trading Pairs
    setTpLoading(true);
    try {
      await dispatch(fetchSpotTradingPairs()).unwrap();
      setTpError(null);
    } catch (err) {
      setTpError(err === 'Unauthorized' ? 'Please log in again.' : 'Failed to fetch trading pairs.');
      if (err === 'Unauthorized') {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    } finally {
      setTpLoading(false);
    }

    // Wallet IDs with retry/backoff
    let attempt = 0;
    const max = 3;
    const run = async () => {
      attempt++;
      setWalletLoading(true);
      try {
        await dispatch(fetchWalletIds({ userId: user._id })).unwrap();
        setWalletError(null);
      } catch (err) {
        if (err?.response?.status === 401 || err === 'Unauthorized') {
          setWalletError('Unauthorized: Please log in again.');
          localStorage.removeItem('token');
          window.location.href = '/login';
        } else if (attempt < max) {
          const delay = 800 * attempt; // backoff
          setTimeout(run, delay);
        } else {
          setWalletError('Failed to fetch wallet IDs. Try again.');
        }
      } finally {
        setWalletLoading(false);
      }
    };
    run();

    setFetching(false);
  }, [dispatch, hasInit, userLoading, user?._id, getAuthOk]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // default coin
  const defaultCoin = useMemo(() => spotTradingPairs?.[0]?.symbol || null, [spotTradingPairs]);
  useEffect(() => {
    if (defaultCoin && !selectedCoin) dispatch(setSelectedCoin(defaultCoin));
  }, [dispatch, defaultCoin, selectedCoin]);

  const futuresAssets = useMemo(() => {
    return spotTradingPairs?.map((pair) => ({
      symbol: pair.base_asset_id?.symbol,
      name: pair.base_asset_id?.name,
    })) || [];
  }, [spotTradingPairs]);

  const anyError = futuresError || socketError || tpError || walletError;
  const topBarLoading = tpLoading || walletLoading || userLoading || fetching;

  return (
    <>
      <Snackbar
        open={!!anyError}
        autoHideDuration={6000}
        onClose={() => { /* keep visible until user retries */ }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {anyError}
        </Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        {!isSmDown && <TopNav />}

        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mb: isSmDown ? '60px' : 0 }}>
          {/* Top loading bar */}
          {topBarLoading && <LinearProgress sx={{ height: 2 }} />}

          {/* Header / pair strip */}
          <Box sx={{ px: 2, pt: isSmDown ? 1 : 10, pb: 1 }}>
            {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                Futures
              </Typography>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={() => fetchAll()}
                  size="small"
                  sx={{ color: theme.palette.mode === 'dark' ? COLORS.YELLOW : 'text.primary' }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box> */}

            {!tpLoading && !tpError ? (
              <FuturesCoinDetails futuresAssets={futuresAssets} />
            ) : (
              <Box sx={{ display: 'flex', gap: 2, py: 1 }}>
                <Skeleton variant="text" width={120} height={32} />
                <Skeleton variant="text" width={180} height={32} />
                <Skeleton variant="text" width={160} height={32} />
              </Box>
            )}
          </Box>

          {/* Main content grid – Binance-like */}
          <Box sx={{ px: 2, pb: 2 }}>
            <Grid container spacing={1}>
              {/* Left: Chart + OrderBook */}
              <Grid item xs={12} lg={9}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 1, height: isLgUp ? 520 : isMd ? 420 : 360, bgcolor: 'background.paper' }}>
                      {tpLoading || userLoading ? (
                        <Skeleton variant="rectangular" sx={{ height: '100%', borderRadius: 1 }} />
                      ) : (
                        <TradingViewWidget />
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 1, height: isLgUp ? 520 : isMd ? 420 : 360, bgcolor: 'background.paper' }}>
                      {selectedCoin ? (
                        <FuturesOrderBook />
                      ) : (
                        <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                          <Typography sx={{ color: 'text.secondary' }}>
                            Select a coin to view Order Book
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper sx={{ p: 1, bgcolor: 'background.paper' }}>
                      <FuturesTradeTabs />
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>

              {/* Right: Order Form */}
              <Grid item xs={12} lg={3}>
                <Paper sx={{ p: 1, height: isLgUp ? 520 : 'auto', bgcolor: 'background.paper', position: 'sticky', top: isSmDown ? 0 : 88 }}>
                  {selectedCoin ? (
                    <FuturesOrderForm />
                  ) : (
                    <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                      <Typography sx={{ color: 'text.secondary' }}>
                        Select a coin to place an order
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {isSmDown && <BottomNav />}
        </Box>
      </Box>
    </>
  );
};

export default Futures;
